/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/admin/DataCleanupModal.tsx
 * Release: v2.2.0 - Modale pulizia database + preview draggable + cancellazione selettiva
 * Data: 2026-02-10
 * Descrizione: Modale per cancellazione selettiva dati (solo superadmin)
 *              con ResizableModal draggable per visualizzare tutti i dati,
 *              checkbox per riga e cancellazione selettiva
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trash2,
  AlertTriangle,
  Users,
  Settings2,
  FileText,
  Camera,
  Loader2,
  CheckCircle,
  XCircle,
  X,
  ShieldCheck,
  Smartphone,
  UserCog,
  Package,
  Boxes,
  Eye,
  Key,
  MessageSquare,
  Database
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import ResizableModal from '../ui/ResizableModal';

// ============================================================================
// Types
// ============================================================================

interface DataCounts {
  // Clienti
  clienti_v2_users: number;
  clienti_profiles: number;
  clienti_v2_profiles: number;
  clienti_temporanei: number;
  // Turbine
  turbine: number;
  mezzi: number;
  turbine_metadata: number;
  checkin: number;
  component_damage_photos: number;
  turbine_workflow_log: number;
  turbine_stato_log: number;
  turbine_knowledge: number;
  turbine_state_history: number;
  turbine_year_counters: number;
  workflow_storico: number;
  workflow_actions_log: number;
  workflow_sessions: number;
  workflow_session_apparati: number;
  // Analisi e Preventivi
  analisi_pending: number;
  preventivi: number;
  preventivi_ricambi: number;
  preventivi_storico_stati: number;
  preventivi_archivio: number;
  // Foto
  sessioni_foto: number;
  foto_generiche: number;
  photo_sessions_legacy: number;
  photo_requests: number;
  photo_request_events: number;
  photo_request_uploads: number;
  // Operatori
  operatori_mobile: number;
  mobile_sessions: number;
  mobile_login_audit: number;
  mobile_login_tokens: number;
  mobile_connections: number;
  // Utenti
  utenti_non_admin: number;
  // Sessioni
  v2_refresh_tokens: number;
  refresh_tokens: number;
  online_users: number;
  audit_log: number;
  push_subscriptions: number;
  push_test_notifications: number;
  // Chat
  chat_messages: number;
  phone_sessions: number;
  session_chat: number;
  user_messages: number;
  user_conversations: number;
  // Magazzino
  magazzino_ricambi: number;
  magazzino_stock: number;
  magazzino_fornitori: number;
  magazzino_ordini: number;
  magazzino_ordini_righe: number;
  magazzino_ordini_invii: number;
  magazzino_ordini_archivio: number;
  magazzino_consegne: number;
  magazzino_consegne_righe: number;
  magazzino_trasferimenti: number;
  magazzino_trasferimenti_righe: number;
  magazzino_distinte: number;
  magazzino_distinte_componenti: number;
  magazzino_inventari: number;
  magazzino_inventari_righe: number;
  magazzino_movimenti: number;
  magazzino_workflow_log: number;
  magazzino_year_counters: number;
  // Magazzino Config
  magazzino_categorie: number;
  magazzino_unita: number;
  magazzino_posizioni: number;
}

interface DeleteResult {
  [key: string]: number;
}

interface PreviewTable {
  tableName: string;
  columns: string[];
  rows: Record<string, any>[];
  totalCount: number;
}

type TargetKey = 'clienti' | 'turbine' | 'analisi' | 'preventivi' | 'foto' |
                 'operatori' | 'utenti' | 'sessioni' | 'chat' | 'magazzino' | 'magazzino_config';

const TARGET_LABELS: Record<TargetKey, string> = {
  clienti: 'Clienti',
  turbine: 'Turbine, Mezzi e Workflow',
  analisi: 'Analisi',
  preventivi: 'Preventivi',
  foto: 'Foto e Sessioni',
  operatori: 'Operatori Mobile',
  utenti: 'Altri Utenti',
  sessioni: 'Sessioni e Token',
  chat: 'Chat e Messaggi',
  magazzino: 'Magazzino Dati',
  magazzino_config: 'Magazzino Config'
};

// ============================================================================
// Mappa display name → nome reale tabella DB (per delete-rows)
// ============================================================================
const TABLE_NAME_MAP: Record<string, string> = {
  'v2_users (clienti)': 'v2_users',
  'v2_users (non-admin)': 'v2_users',
  'photo_sessions (legacy)': 'photo_sessions'
};

const getRealTableName = (displayName: string): string =>
  TABLE_NAME_MAP[displayName] || displayName;

// ============================================================================
// Descrizioni sintetiche tabelle
// ============================================================================
const TABLE_DESCRIPTIONS: Record<string, string> = {
  // Clienti
  'v2_users (clienti)': 'Utenti registrati come clienti',
  'customer_profiles': 'Profili dettagliati clienti (anagrafica, contatti)',
  'v2_customer_profiles': 'Profili clienti v2',
  'business_card_temp': 'Biglietti da visita scansionati in attesa di elaborazione',
  // Turbine
  'turbine': 'Apparati registrati nel sistema (accettazione)',
  'mezzi': 'Veicoli associati ai clienti',
  'checkin': 'Check-in apparati in accettazione',
  'turbine_temp_metadata': 'Metadati temporanei apparati (foto JSON legacy)',
  'component_damage_photos': 'Foto danni componenti',
  'workflow_storico': 'Storico spostamenti apparati nel workflow',
  'turbine_workflow_log': 'Log operazioni workflow apparati',
  'turbine_stato_log': 'Log cambiamenti stato apparati',
  'turbine_knowledge': 'Knowledge base apparati',
  'turbine_state_history': 'Storico stati dual-track workflow',
  'turbine_year_counters': 'Contatori progressivi annuali apparati',
  'workflow_actions_log': 'Log azioni workflow',
  'workflow_sessions': 'Sessioni workflow accettazione',
  'workflow_session_apparati': 'Apparati associati alle sessioni workflow',
  // Analisi
  'mobile_analisi_pending': 'Analisi guasti in corso da operatori mobile',
  'analisi_guasti_bozze': 'Bozze analisi guasti salvate',
  // Preventivi
  'preventivi': 'Preventivi generati per i clienti',
  'preventivi_ricambi': 'Righe ricambi inserite nei preventivi',
  'preventivi_storico_stati': 'Storico cambiamenti stato preventivi',
  'preventivi_archivio': 'Preventivi archiviati/rifiutati',
  // Foto
  'photo_sessions_generic': 'Sessioni foto unificate (accettazione, danni, ecc.)',
  'photo_sessions_generic_photos': 'Singole foto nelle sessioni generiche',
  'photo_sessions (legacy)': 'Sessioni foto vecchio sistema',
  'photo_requests': 'Richieste foto remote da PC a mobile',
  'photo_request_events': 'Eventi nelle richieste foto remote',
  'photo_request_uploads': 'Upload foto nelle richieste remote',
  // Operatori
  'mobile_operators': 'Operatori registrati per app mobile',
  'mobile_sessions': 'Sessioni attive operatori mobile',
  'mobile_login_audit': 'Log accessi/logout operatori mobile',
  'mobile_operator_login_tokens': 'Token login operatori mobile',
  'mobile_connections': 'Connessioni WebSocket operatori',
  // Utenti
  'v2_users (non-admin)': 'Utenti non-admin e non-client',
  // Sessioni
  'v2_refresh_tokens': 'Token di refresh autenticazione v2',
  'refresh_tokens': 'Token di refresh autenticazione legacy',
  'online_users': 'Utenti attualmente online',
  'audit_log': 'Log azioni utenti nel sistema',
  'push_subscriptions': 'Iscrizioni notifiche push browser',
  'push_test_notifications': 'Notifiche push di test',
  // Chat
  'user_messages': 'Messaggi diretti tra utenti',
  'user_conversations': 'Conversazioni tra utenti',
  'persistent_chat_messages': 'Messaggi chat persistenti (WhatsApp bot)',
  'persistent_phone_sessions': 'Sessioni telefoniche persistenti',
  'persistent_session_chat': 'Chat associate a sessioni telefoniche',
  // Magazzino
  'magazzino_ricambi': 'Anagrafica ricambi magazzino',
  'magazzino_stock': 'Giacenze ricambi per sede',
  'magazzino_fornitori': 'Anagrafica fornitori ricambi',
  'magazzino_ordini': 'Ordini a fornitori',
  'magazzino_ordini_righe': 'Righe dettaglio ordini fornitori',
  'magazzino_ordini_invii': 'Tracking invii ordini',
  'magazzino_ordini_archivio': 'Ordini archiviati',
  'magazzino_consegne': 'Consegne ricambi ricevute',
  'magazzino_consegne_righe': 'Righe dettaglio consegne',
  'magazzino_trasferimenti': 'Trasferimenti ricambi tra sedi',
  'magazzino_trasferimenti_righe': 'Righe dettaglio trasferimenti',
  'magazzino_distinte': 'Distinte base componenti',
  'magazzino_distinte_componenti': 'Componenti nelle distinte base',
  'magazzino_inventari': 'Inventari fisici magazzino',
  'magazzino_inventari_righe': 'Righe conteggio inventari',
  'magazzino_movimenti': 'Movimenti magazzino (carico/scarico)',
  'magazzino_workflow_log': 'Log operazioni workflow magazzino',
  'magazzino_year_counters': 'Contatori progressivi annuali magazzino',
  // Magazzino Config
  'magazzino_categorie': 'Categorie ricambi (classificazione)',
  'magazzino_unita': 'Unita di stoccaggio (scaffali, armadi)',
  'magazzino_posizioni': 'Posizioni nei scaffali (piano, cestello)'
};

// ============================================================================
// DataCleanupModal - Componente principale
// ============================================================================

interface DataCleanupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DataCleanupModal: React.FC<DataCleanupModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [counts, setCounts] = useState<DataCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTargets, setSelectedTargets] = useState<Set<TargetKey>>(new Set());
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<DeleteResult | null>(null);
  const [showConfirmStep, setShowConfirmStep] = useState(false);
  // Preview modal state
  const [previewTarget, setPreviewTarget] = useState<TargetKey | null>(null);
  const [previewData, setPreviewData] = useState<PreviewTable[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const isSuperAdmin = user?.role === 'superadmin' || user?.group_id === 'superadmin';

  const authHeaders: Record<string, string> = {
    'x-user-id': user?.id || '',
    'x-user-role': user?.role || '',
    'x-user-group': user?.group_id || '',
    'x-user-name': user?.name || user?.email || ''
  };

  const loadCounts = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/data-cleanup/counts', {
        headers: authHeaders
      });
      if (!response.ok) {
        if (response.status === 403) throw new Error('Accesso negato. Richiesto ruolo superadmin.');
        throw new Error('Errore nel caricamento dei conteggi');
      }
      const data = await response.json();
      if (data.success) {
        setCounts(data.counts);
      } else {
        throw new Error(data.error || 'Errore sconosciuto');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (isOpen && isSuperAdmin) {
      loadCounts();
      setSelectedTargets(new Set());
      setConfirmPhrase('');
      setDeleteResult(null);
      setShowConfirmStep(false);
      setPreviewTarget(null);
      setPreviewData([]);
    }
  }, [isOpen, loadCounts, isSuperAdmin]);

  const toggleTarget = (target: TargetKey) => {
    const newSet = new Set(selectedTargets);
    if (newSet.has(target)) {
      newSet.delete(target);
    } else {
      newSet.add(target);
    }
    setSelectedTargets(newSet);
  };

  const openPreview = async (target: TargetKey) => {
    setPreviewTarget(target);
    setPreviewLoading(true);
    setPreviewData([]);
    try {
      const response = await fetch(`/api/admin/data-cleanup/preview/${target}`, {
        headers: authHeaders
      });
      const data = await response.json();
      if (data.success) {
        setPreviewData(data.tables || []);
      }
    } catch {
      setPreviewData([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewTarget(null);
    setPreviewData([]);
  };

  const executeDelete = async () => {
    if (confirmPhrase !== 'ELIMINA TUTTO') {
      setError('Frase di conferma non corretta');
      return;
    }
    setIsDeleting(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/data-cleanup/execute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          targets: Array.from(selectedTargets),
          confirmPhrase
        })
      });
      const data = await response.json();
      if (data.success) {
        setDeleteResult(data.deleted);
        setShowConfirmStep(false);
        setSelectedTargets(new Set());
        setConfirmPhrase('');
        loadCounts();
      } else {
        throw new Error(data.error || 'Errore durante la cancellazione');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setIsDeleting(false);
    }
  };

  const calculateSelectedTotal = () => {
    if (!counts) return 0;
    let total = 0;
    const c = counts;
    if (selectedTargets.has('clienti'))
      total += c.clienti_v2_users + c.clienti_profiles + (c.clienti_v2_profiles || 0) + c.clienti_temporanei;
    if (selectedTargets.has('turbine'))
      total += c.turbine + c.mezzi + (c.turbine_metadata || 0) + (c.checkin || 0) +
               (c.component_damage_photos || 0) + (c.turbine_workflow_log || 0) +
               (c.workflow_storico || 0) + (c.workflow_actions_log || 0) +
               (c.workflow_sessions || 0);
    if (selectedTargets.has('analisi'))
      total += c.analisi_pending;
    if (selectedTargets.has('preventivi'))
      total += c.preventivi + (c.preventivi_ricambi || 0) + (c.preventivi_storico_stati || 0) + (c.preventivi_archivio || 0);
    if (selectedTargets.has('foto'))
      total += (c.sessioni_foto || 0) + (c.foto_generiche || 0) + (c.photo_sessions_legacy || 0) +
               (c.photo_requests || 0) + (c.photo_request_events || 0) + (c.photo_request_uploads || 0);
    if (selectedTargets.has('operatori'))
      total += c.operatori_mobile + (c.mobile_sessions || 0) + (c.mobile_login_audit || 0) + (c.mobile_login_tokens || 0);
    if (selectedTargets.has('utenti'))
      total += c.utenti_non_admin;
    if (selectedTargets.has('sessioni'))
      total += (c.v2_refresh_tokens || 0) + (c.refresh_tokens || 0) + (c.online_users || 0) +
               (c.audit_log || 0) + (c.push_subscriptions || 0) + (c.push_test_notifications || 0);
    if (selectedTargets.has('chat'))
      total += (c.chat_messages || 0) + (c.phone_sessions || 0) + (c.session_chat || 0) +
               (c.user_messages || 0) + (c.user_conversations || 0);
    if (selectedTargets.has('magazzino'))
      total += (c.magazzino_ricambi || 0) + (c.magazzino_stock || 0) + (c.magazzino_fornitori || 0) +
               (c.magazzino_ordini || 0) + (c.magazzino_ordini_righe || 0) + (c.magazzino_consegne || 0) +
               (c.magazzino_trasferimenti || 0) + (c.magazzino_distinte || 0) +
               (c.magazzino_inventari || 0) + (c.magazzino_movimenti || 0);
    if (selectedTargets.has('magazzino_config'))
      total += (c.magazzino_categorie || 0) + (c.magazzino_unita || 0) + (c.magazzino_posizioni || 0);
    return total;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Preview modal (ResizableModal sopra) */}
      {previewTarget && (
        <PreviewModal
          target={previewTarget}
          tables={previewData}
          loading={previewLoading}
          onClose={closePreview}
          authHeaders={authHeaders}
          onDataChanged={() => {
            loadCounts();
            openPreview(previewTarget);
          }}
        />
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-800 rounded-2xl shadow-2xl border border-zinc-700 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-700 bg-zinc-900/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-500/20 rounded-lg">
                    <Trash2 className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Elimina Dati</h2>
                    <p className="text-xs text-gray-400">Pulizia database (solo superadmin)</p>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-zinc-700 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Info Superadmin */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 mb-4">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-green-500" />
                    <span className="text-green-400 text-sm font-medium">
                      Gli account Superadmin sono sempre preservati
                    </span>
                  </div>
                </div>

                {/* Warning */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-amber-200/80 text-sm">
                      Le cancellazioni sono <strong>irreversibili</strong>. Usa il pulsante
                      <Eye className="w-3.5 h-3.5 inline mx-1 -mt-0.5" /> per visualizzare i dati prima di eliminare.
                    </p>
                  </div>
                </div>

                {/* Loading */}
                {loading && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-cyan-500 animate-spin" />
                    <span className="ml-2 text-gray-400 text-sm">Caricamento...</span>
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-5 h-5 text-red-500" />
                      <span className="text-red-400 text-sm">{error}</span>
                    </div>
                  </div>
                )}

                {/* Success Result */}
                {deleteResult && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-4"
                  >
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-green-400 mb-2">Cancellazione completata!</h3>
                        <ul className="text-green-200/80 text-sm space-y-1">
                          {Object.entries(deleteResult).map(([key, value]) => (
                            <li key={key}>
                              {key.replace(/_/g, ' ')}: <strong>{value}</strong> eliminati
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Data Cards */}
                {counts && !loading && !showConfirmStep && (
                  <div className="space-y-2">
                    <DataCard
                      title="Clienti"
                      icon={Users}
                      selected={selectedTargets.has('clienti')}
                      onToggle={() => toggleTarget('clienti')}
                      onPreview={() => openPreview('clienti')}
                      items={[
                        { label: 'Utenti', count: counts.clienti_v2_users },
                        { label: 'Profili', count: counts.clienti_profiles + (counts.clienti_v2_profiles || 0) },
                        { label: 'Business Card', count: counts.clienti_temporanei }
                      ]}
                      color="blue"
                    />

                    <DataCard
                      title="Turbine, Mezzi e Workflow"
                      icon={Settings2}
                      selected={selectedTargets.has('turbine')}
                      onToggle={() => toggleTarget('turbine')}
                      onPreview={() => openPreview('turbine')}
                      items={[
                        { label: 'Turbine', count: counts.turbine },
                        { label: 'Mezzi', count: counts.mezzi },
                        { label: 'Checkin', count: counts.checkin || 0 },
                        { label: 'WF Storico', count: (counts.workflow_storico || 0) + (counts.turbine_workflow_log || 0) }
                      ]}
                      color="purple"
                    />

                    <DataCard
                      title="Analisi"
                      icon={FileText}
                      selected={selectedTargets.has('analisi')}
                      onToggle={() => toggleTarget('analisi')}
                      onPreview={() => openPreview('analisi')}
                      items={[
                        { label: 'Pending', count: counts.analisi_pending }
                      ]}
                      color="amber"
                    />

                    <DataCard
                      title="Preventivi"
                      icon={FileText}
                      selected={selectedTargets.has('preventivi')}
                      onToggle={() => toggleTarget('preventivi')}
                      onPreview={() => openPreview('preventivi')}
                      items={[
                        { label: 'Preventivi', count: counts.preventivi },
                        { label: 'Ricambi', count: counts.preventivi_ricambi || 0 },
                        { label: 'Storico', count: counts.preventivi_storico_stati || 0 },
                        { label: 'Archivio', count: counts.preventivi_archivio || 0 }
                      ]}
                      color="green"
                    />

                    <DataCard
                      title="Foto e Sessioni"
                      icon={Camera}
                      selected={selectedTargets.has('foto')}
                      onToggle={() => toggleTarget('foto')}
                      onPreview={() => openPreview('foto')}
                      items={[
                        { label: 'Sessioni', count: counts.sessioni_foto || 0 },
                        { label: 'Foto', count: counts.foto_generiche || 0 },
                        { label: 'Legacy', count: counts.photo_sessions_legacy || 0 },
                        { label: 'Requests', count: counts.photo_requests || 0 }
                      ]}
                      color="pink"
                    />

                    <DataCard
                      title="Operatori Mobile"
                      icon={Smartphone}
                      selected={selectedTargets.has('operatori')}
                      onToggle={() => toggleTarget('operatori')}
                      onPreview={() => openPreview('operatori')}
                      items={[
                        { label: 'Operatori', count: counts.operatori_mobile },
                        { label: 'Sessioni', count: counts.mobile_sessions || 0 },
                        { label: 'Audit', count: counts.mobile_login_audit || 0 },
                        { label: 'Token', count: counts.mobile_login_tokens || 0 }
                      ]}
                      color="orange"
                    />

                    <DataCard
                      title="Altri Utenti"
                      icon={UserCog}
                      selected={selectedTargets.has('utenti')}
                      onToggle={() => toggleTarget('utenti')}
                      onPreview={() => openPreview('utenti')}
                      items={[
                        { label: 'Utenti (no superadmin/client)', count: counts.utenti_non_admin }
                      ]}
                      color="red"
                    />

                    {/* Separatore */}
                    <div className="border-t border-zinc-600 my-3 pt-2">
                      <span className="text-xs text-zinc-500 uppercase tracking-wider">Sessioni e Comunicazioni</span>
                    </div>

                    <DataCard
                      title="Sessioni e Token"
                      icon={Key}
                      selected={selectedTargets.has('sessioni')}
                      onToggle={() => toggleTarget('sessioni')}
                      onPreview={() => openPreview('sessioni')}
                      items={[
                        { label: 'Refresh Token', count: (counts.v2_refresh_tokens || 0) + (counts.refresh_tokens || 0) },
                        { label: 'Online', count: counts.online_users || 0 },
                        { label: 'Push', count: (counts.push_subscriptions || 0) + (counts.push_test_notifications || 0) },
                        { label: 'Audit', count: counts.audit_log || 0 }
                      ]}
                      color="amber"
                    />

                    <DataCard
                      title="Chat e Messaggi"
                      icon={MessageSquare}
                      selected={selectedTargets.has('chat')}
                      onToggle={() => toggleTarget('chat')}
                      onPreview={() => openPreview('chat')}
                      items={[
                        { label: 'Messaggi', count: (counts.user_messages || 0) + (counts.chat_messages || 0) },
                        { label: 'Conversazioni', count: (counts.user_conversations || 0) + (counts.phone_sessions || 0) }
                      ]}
                      color="blue"
                    />

                    {/* Separatore Magazzino */}
                    <div className="border-t border-zinc-600 my-3 pt-2">
                      <span className="text-xs text-zinc-500 uppercase tracking-wider">Magazzino Ricambi</span>
                    </div>

                    <DataCard
                      title="Magazzino Dati"
                      icon={Package}
                      selected={selectedTargets.has('magazzino')}
                      onToggle={() => toggleTarget('magazzino')}
                      onPreview={() => openPreview('magazzino')}
                      items={[
                        { label: 'Ricambi', count: counts.magazzino_ricambi || 0 },
                        { label: 'Stock', count: counts.magazzino_stock || 0 },
                        { label: 'Ordini', count: counts.magazzino_ordini || 0 },
                        { label: 'Consegne', count: counts.magazzino_consegne || 0 },
                        { label: 'Inventari', count: counts.magazzino_inventari || 0 },
                        { label: 'Movimenti', count: counts.magazzino_movimenti || 0 }
                      ]}
                      color="orange"
                    />

                    <DataCard
                      title="Magazzino Config"
                      icon={Boxes}
                      selected={selectedTargets.has('magazzino_config')}
                      onToggle={() => toggleTarget('magazzino_config')}
                      onPreview={() => openPreview('magazzino_config')}
                      items={[
                        { label: 'Categorie', count: counts.magazzino_categorie || 0 },
                        { label: 'Unita', count: counts.magazzino_unita || 0 },
                        { label: 'Posizioni', count: counts.magazzino_posizioni || 0 }
                      ]}
                      color="amber"
                    />
                  </div>
                )}

                {/* Confirm Step */}
                {showConfirmStep && (
                  <div className="text-center py-4">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <AlertTriangle className="w-8 h-8 text-red-500" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">Conferma Cancellazione</h3>
                    <p className="text-gray-400 mb-4">
                      Stai per eliminare <strong className="text-white">{calculateSelectedTotal()}</strong> record.
                    </p>
                    <div className="max-w-xs mx-auto">
                      <label className="block text-sm text-gray-300 mb-2 text-left">
                        Scrivi <strong className="text-red-400">ELIMINA TUTTO</strong> per confermare:
                      </label>
                      <input
                        type="text"
                        value={confirmPhrase}
                        onChange={(e) => setConfirmPhrase(e.target.value)}
                        placeholder="Scrivi qui..."
                        className="w-full px-4 py-3 bg-zinc-900 border border-zinc-600 rounded-lg text-white focus:outline-none focus:border-red-500 transition-colors"
                        autoFocus
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-zinc-700 bg-zinc-900/50">
                {!showConfirmStep ? (
                  <div className="flex items-center justify-between">
                    <div className="text-gray-400 text-sm">
                      <span className="text-lg font-semibold text-white">{calculateSelectedTotal()}</span> record selezionati
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={onClose}
                        className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors text-sm"
                      >
                        Chiudi
                      </button>
                      <button
                        onClick={() => setShowConfirmStep(true)}
                        disabled={selectedTargets.size === 0}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedTargets.size === 0
                            ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                            : 'bg-red-600 hover:bg-red-500 text-white'
                        }`}
                      >
                        <Trash2 className="w-4 h-4" />
                        Elimina
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => { setShowConfirmStep(false); setConfirmPhrase(''); }}
                      className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors text-sm"
                    >
                      Annulla
                    </button>
                    <button
                      onClick={executeDelete}
                      disabled={confirmPhrase !== 'ELIMINA TUTTO' || isDeleting}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        confirmPhrase === 'ELIMINA TUTTO' && !isDeleting
                          ? 'bg-red-600 hover:bg-red-500 text-white'
                          : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                      }`}
                    >
                      {isDeleting ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Eliminazione...</>
                      ) : (
                        <><Trash2 className="w-4 h-4" /> Conferma</>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ============================================================================
// DataCard - Card compatta con bottone preview
// ============================================================================
interface DataCardProps {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  selected: boolean;
  onToggle: () => void;
  onPreview: () => void;
  items: { label: string; count: number }[];
  color: 'blue' | 'purple' | 'amber' | 'green' | 'pink' | 'orange' | 'red';
}

const DataCard: React.FC<DataCardProps> = ({
  title, icon: Icon, selected, onToggle, onPreview, items, color
}) => {
  const colorClasses = {
    blue: { bg: 'bg-blue-500/20', border: 'border-blue-500/50', text: 'text-blue-400', check: 'bg-blue-500' },
    purple: { bg: 'bg-purple-500/20', border: 'border-purple-500/50', text: 'text-purple-400', check: 'bg-purple-500' },
    amber: { bg: 'bg-amber-500/20', border: 'border-amber-500/50', text: 'text-amber-400', check: 'bg-amber-500' },
    green: { bg: 'bg-green-500/20', border: 'border-green-500/50', text: 'text-green-400', check: 'bg-green-500' },
    pink: { bg: 'bg-pink-500/20', border: 'border-pink-500/50', text: 'text-pink-400', check: 'bg-pink-500' },
    orange: { bg: 'bg-orange-500/20', border: 'border-orange-500/50', text: 'text-orange-400', check: 'bg-orange-500' },
    red: { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-400', check: 'bg-red-500' }
  };

  const colors = colorClasses[color];
  const total = items.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className={`rounded-xl border-2 transition-all ${
      selected ? `${colors.bg} ${colors.border}` : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
    }`}>
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3 cursor-pointer flex-1" onClick={onToggle}>
          <div className={`p-1.5 rounded-lg ${selected ? colors.bg : 'bg-zinc-700'}`}>
            <Icon className={`w-4 h-4 ${selected ? colors.text : 'text-gray-400'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className={`font-medium text-sm ${selected ? colors.text : 'text-white'}`}>{title}</h3>
            <p className="text-xs text-gray-500 truncate">
              {items.map((item, i) => (
                <span key={item.label}>
                  {item.label}: {item.count}{i < items.length - 1 ? ' · ' : ''}
                </span>
              ))}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-sm font-bold ${selected ? colors.text : 'text-gray-400'}`}>
            {total}
          </span>
          {/* Preview button */}
          <button
            onClick={(e) => { e.stopPropagation(); onPreview(); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-cyan-500/20 text-gray-500 hover:text-cyan-400"
            title="Visualizza tutti i dati"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          {/* Checkbox */}
          <div
            onClick={onToggle}
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all cursor-pointer ${
              selected ? `${colors.check} border-transparent` : 'border-zinc-600'
            }`}
          >
            {selected && <CheckCircle className="w-3 h-3 text-white" />}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PreviewModal - ResizableModal draggable per visualizzare tutti i dati
// ============================================================================
interface PreviewModalProps {
  target: TargetKey;
  tables: PreviewTable[];
  loading: boolean;
  onClose: () => void;
  authHeaders: Record<string, string>;
  onDataChanged: () => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  target, tables, loading, onClose, authHeaders, onDataChanged
}) => {
  const totalRows = tables.reduce((sum, t) => sum + t.totalCount, 0);

  return (
    <ResizableModal
      isOpen={true}
      onClose={onClose}
      title={TARGET_LABELS[target]}
      subtitle={
        <span>
          {tables.length} tabelle &middot; <span className="text-cyan-400 font-semibold">{totalRows}</span> record totali
        </span>
      }
      initialWidth={1100}
      initialHeight={700}
      minWidth={600}
      minHeight={400}
      enableResize={true}
      enableDrag={true}
      zIndex={10001}
    >
      <div className="h-full overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
            <span className="ml-3 text-gray-400">Caricamento dati...</span>
          </div>
        ) : tables.length === 0 ? (
          <div className="text-center py-20 text-gray-500">Nessun dato presente</div>
        ) : (
          <div className="space-y-6">
            {tables.map((table) => (
              <PreviewTableSection
                key={table.tableName}
                table={table}
                authHeaders={authHeaders}
                onDataChanged={onDataChanged}
              />
            ))}
          </div>
        )}
      </div>
    </ResizableModal>
  );
};

// ============================================================================
// PreviewTableSection - Singola tabella con header sticky + checkbox + delete
// ============================================================================
interface PreviewTableSectionProps {
  table: PreviewTable;
  authHeaders: Record<string, string>;
  onDataChanged: () => void;
}

const PreviewTableSection: React.FC<PreviewTableSectionProps> = ({ table, authHeaders, onDataChanged }) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const hasIdColumn = table.columns.includes('id');
  const allIds = hasIdColumn ? table.rows.map(r => String(r.id)).filter(id => id && id !== 'undefined' && id !== 'null') : [];
  const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));

  const toggleId = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const realTableName = getRealTableName(table.tableName);
      // Invia IDs come numeri se sono numerici, altrimenti come stringhe
      const rawIds = Array.from(selectedIds);
      const parsedIds = rawIds.map(id => {
        const num = Number(id);
        return !isNaN(num) && String(num) === id ? num : id;
      });
      const response = await fetch('/api/admin/data-cleanup/delete-rows', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          tableName: realTableName,
          ids: parsedIds
        })
      });
      const data = await response.json();
      if (data.success) {
        setSelectedIds(new Set());
        onDataChanged();
      } else {
        setDeleteError(data.error || 'Errore durante la cancellazione');
      }
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setDeleting(false);
    }
  };

  const description = TABLE_DESCRIPTIONS[table.tableName] || '';
  const realName = getRealTableName(table.tableName);
  const isCascadeTable = realName === 'turbine' || realName === 'v2_users';

  return (
    <div className="bg-zinc-800 rounded-xl border border-zinc-700 overflow-hidden">
      {/* Header tabella */}
      <div className="flex items-center justify-between px-4 py-3 bg-zinc-800 border-b border-zinc-700">
        <div className="flex items-center gap-3 min-w-0">
          <Database className="w-4 h-4 text-cyan-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-cyan-400">{table.tableName}</span>
              <span className="text-xs text-gray-500 bg-zinc-700 px-2 py-0.5 rounded-full flex-shrink-0">
                {table.totalCount} righe
              </span>
            </div>
            {description && (
              <p className="text-[11px] text-gray-500 mt-0.5 truncate">{description}</p>
            )}
          </div>
        </div>
        {/* Bottone elimina selezionati */}
        {hasIdColumn && selectedIds.size > 0 && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {isCascadeTable && (
              <span className="text-[10px] text-amber-400" title="Cancella anche mezzo, checkin, preventivi, foto, analisi, workflow collegati">
                + dati correlati
              </span>
            )}
            <button
              onClick={deleteSelected}
              disabled={deleting}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
            >
              {deleting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Trash2 className="w-3.5 h-3.5" />
              )}
              Elimina {selectedIds.size}
            </button>
          </div>
        )}
      </div>

      {/* Errore delete */}
      {deleteError && (
        <div className="px-4 py-2 bg-red-500/10 border-b border-red-500/30">
          <span className="text-xs text-red-400">{deleteError}</span>
        </div>
      )}

      {/* Tabella con header sticky */}
      <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10">
            <tr className="bg-zinc-900 border-b border-zinc-600">
              {/* Checkbox select all */}
              {hasIdColumn && (
                <th className="px-2 py-2.5 bg-zinc-900 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="w-3.5 h-3.5 rounded border-zinc-500 bg-zinc-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0 cursor-pointer"
                  />
                </th>
              )}
              <th className="px-3 py-2.5 text-left text-gray-300 font-semibold whitespace-nowrap bg-zinc-900 text-[11px] uppercase tracking-wider">
                #
              </th>
              {table.columns.map((col) => (
                <th key={col} className="px-3 py-2.5 text-left text-gray-300 font-semibold whitespace-nowrap bg-zinc-900 text-[11px] uppercase tracking-wider">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, i) => {
              const rowId = hasIdColumn ? String(row.id) : '';
              const isSelected = hasIdColumn && selectedIds.has(rowId);

              return (
                <tr
                  key={i}
                  className={`border-b border-zinc-800/50 transition-colors ${
                    isSelected
                      ? 'bg-red-500/10 hover:bg-red-500/20'
                      : 'hover:bg-zinc-700/30'
                  }`}
                >
                  {/* Checkbox riga */}
                  {hasIdColumn && (
                    <td className="px-2 py-1.5">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleId(rowId)}
                        className="w-3.5 h-3.5 rounded border-zinc-500 bg-zinc-800 text-red-500 focus:ring-red-500 focus:ring-offset-0 cursor-pointer"
                      />
                    </td>
                  )}
                  <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap font-mono text-[10px]">
                    {i + 1}
                  </td>
                  {table.columns.map((col) => (
                    <td key={col} className="px-3 py-1.5 text-gray-300 whitespace-nowrap max-w-[300px] truncate" title={formatCellValue(row[col])}>
                      {formatCellValue(row[col])}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

function formatCellValue(value: any): string {
  if (value === null || value === undefined) return '\u2014';
  if (typeof value === 'boolean') return value ? 'Si' : 'No';
  if (value instanceof Date) return value.toLocaleString('it-IT');
  if (typeof value === 'object') return JSON.stringify(value).slice(0, 100);
  const str = String(value);
  return str.length > 100 ? str.slice(0, 97) + '...' : str;
}

export default DataCleanupModal;
